"use client";

import React, { useMemo } from "react";
import Reveal from "@/components/common/Reveal";
import { Thermometer, Grid2x2 as Grid, Funnel as Filter, Layers, ShieldCheck, FileSpreadsheet, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import DataTable, { type DataTableColumn } from "@/components/common/DataTable";
import InlineCta from "@/components/common/InlineCta";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
interface PolymerSpec {
  polymer: Record<Lang, string>;
  shortName: string;
  recommendedMesh: Record<Lang, string>;
  targetMicron: string;
  recommendedAlloy: string;
  keyContaminants: Record<Lang, string>;
}
const polymerData: PolymerSpec[] = [{
  polymer: {
    en: "Polyethylene Terephthalate",
    zh: "聚对苯二甲酸乙二醇酯 (PET)",
    es: "Tereftalato de polietileno (PET)",
    de: "Polyethylenterephthalat (PET)",
    fr: "Polyéthylène téréphtalate (PET)",
    pt: "Polietileno tereftalato (PET)"
  },
  shortName: "PET",
  recommendedMesh: {
    en: "200 to 400 Mesh Twill Dutch",
    zh: "200 至 400 目斜纹荷兰织",
    es: "Malla holandesa cruzada 200 a 400 mesh",
    de: "200 bis 400 Mesh Köpertresse",
    fr: "Toile hollandaise croisée 200 à 400 mesh",
    pt: "Malha holandesa cruzada 200 a 400 mesh"
  },
  targetMicron: "20 - 40 µm",
  recommendedAlloy: "AISI 316L / 310S",
  keyContaminants: {
    en: "Degraded gels, crosslinked carbon, catalyst traces",
    zh: "降解凝胶物、交联碳点、催化剂微粒",
    es: "Geles degradados, partículas de carbono reticuladas, trazas de catalizador",
    de: "Degradierte Gele, vernetzte Kohlenstoffpartikel, Katalysatorspuren",
    fr: "Gels dégradés, carbone réticulé, traces de catalyseur",
    pt: "Géis degradados, carbono reticulado, traços de catalisador"
  }
}, {
  polymer: {
    en: "Polypropylene & Polyethylene",
    zh: "聚丙烯与聚乙烯 (PP / PE / HDPE)",
    es: "Polipropileno y polietileno (PP / PE / HDPE)",
    de: "Polypropylen & Polyethylen (PP / PE / HDPE)",
    fr: "Polypropylène et polyéthylène (PP / PE / PEHD)",
    pt: "Polipropileno e polietileno (PP / PE / PEAD)"
  },
  shortName: "PP / PE / HDPE",
  recommendedMesh: {
    en: "80 to 250 Mesh Plain Dutch / Square",
    zh: "80 至 250 目平纹荷兰织 / 平纹方眼网",
    es: "Malla lisa holandesa / cuadrada de 80 a 250 mesh",
    de: "80 bis 250 Mesh Glatte Tresse / Quadratmasche",
    fr: "Toile hollandaise unie / carrée de 80 à 250 mesh",
    pt: "Malha lisa holandesa / quadrada de 80 a 250 mesh"
  },
  targetMicron: "40 - 150 µm",
  recommendedAlloy: "AISI 304 / 316L",
  keyContaminants: {
    en: "Unmelted masterbatch, degraded fines, dust",
    zh: "未熔色母颗粒、降解粉末、粉尘",
    es: "Concentrado de color no fundido, finos degradados, polvo",
    de: "Ungeschmolzenes Masterbatch, Feinstoffdegradation, Staub",
    fr: "Mélange maître non fondu, fines dégradées, poussières",
    pt: "Masterbatch não fundido, finos degradados, poeira"
  }
}, {
  polymer: {
    en: "Polyamide (Nylon 6 / 66)",
    zh: "聚酰胺尼龙 (PA6 / PA66)",
    es: "Poliamida (Nailon 6 / 66)",
    de: "Polyamid (Nylon 6 / 66)",
    fr: "Polyamide (Nylon 6 / 66)",
    pt: "Poliamida (Nylon 6 / 66)"
  },
  shortName: "PA6 / PA66",
  recommendedMesh: {
    en: "250 to 500 Mesh Multi-layer Sintered",
    zh: "250 至 500 目多层烧结网",
    es: "Malla sinterizada multicapa de 250 a 500 mesh",
    de: "250 bis 500 Mesh Mehrlagen-Sintergewebe",
    fr: "Toile frittée multicouche 250 à 500 mesh",
    pt: "Malha sinterizada multicamada de 250 a 500 mesh"
  },
  targetMicron: "10 - 25 µm",
  recommendedAlloy: "AISI 316L",
  keyContaminants: {
    en: "Thermal degradation particulates, carbon flecks",
    zh: "热降解微粒、积碳斑点",
    es: "Partículas por degradación térmica, motas de carbón",
    de: "Thermische Abbaupartikel, Kohlenstoffpunkte",
    fr: "Particules de dégradation thermique, points de carbone",
    pt: "Partículas de degradação térmica, pontos de carbono"
  }
}, {
  polymer: {
    en: "Polycarbonate / PMMA",
    zh: "聚碳酸酯 / 亚克力 (PC / PMMA)",
    es: "Policarbonato / PMMA (PC / PMMA)",
    de: "Polycarbonat / PMMA (PC / PMMA)",
    fr: "Polycarbonate / PMMA (PC / PMMA)",
    pt: "Policarbonato / PMMA (PC / PMMA)"
  },
  shortName: "PC / PMMA",
  recommendedMesh: {
    en: "150 to 325 Mesh Dutch Weave",
    zh: "150 至 325 目密纹荷兰网",
    es: "Malla holandesa de 150 a 325 mesh",
    de: "150 bis 325 Mesh Tressengewebe",
    fr: "Toile hollandaise 150 à 325 mesh",
    pt: "Malha holandesa de 150 a 325 mesh"
  },
  targetMicron: "15 - 50 µm",
  recommendedAlloy: "AISI 316L",
  keyContaminants: {
    en: "Black specks, charred particles, oligomers",
    zh: "黑点、焦化物、低聚物",
    es: "Puntos negros, partículas carbonizadas, oligómeros",
    de: "Schwarze Punkte, verkohlte Partikel, Oligomere",
    fr: "Points noirs, particules calcinées, oligomères",
    pt: "Pontos pretos, partículas carbonizadas, oligômeros"
  }
}, {
  polymer: {
    en: "Fluoropolymers & High Performance",
    zh: "氟聚合物与高性能工程塑料 (PTFE / PEEK / PVDF)",
    es: "Fluoropolímeros y polímeros de alto rendimiento (PTFE / PEEK / PVDF)",
    de: "Fluorpolymere & Hochleistungskunststoffe (PTFE / PEEK / PVDF)",
    fr: "Fluoropolymères et polymères haute performance (PTFE / PEEK / PVDF)",
    pt: "Fluorpolímeros e plásticos de alto desempenho (PTFE / PEEK / PVDF)"
  },
  shortName: "PTFE / PEEK / PVDF",
  recommendedMesh: {
    en: "Sintered Porous Laminate / 5-Layer",
    zh: "烧结多孔层压网 / 5层复合网",
    es: "Laminado poroso sinterizado / 5 capas",
    de: "Poröses Sinterlaminat / 5-lagig",
    fr: "Laminé poreux fritté / 5 couches",
    pt: "Laminado poroso sinterizado / 5 camadas"
  },
  targetMicron: "5 - 20 µm",
  recommendedAlloy: "AISI 310S / Inconel 600",
  keyContaminants: {
    en: "High-heat degraded gels, corrosive byproducts",
    zh: "高温降解凝胶物、腐蚀性副产物",
    es: "Geles degradados por alta temperatura, subproductos corrosivos",
    de: "Hochtemperatur-Gele, korrosive Spaltprodukte",
    fr: "Gels dégradés haute température, sous-produits corrosifs",
    pt: "Géis degradados por alta temperatura, subprodutos corrosivos"
  }
}];
interface CopySection {
  eyebrow: string;
  h2: string;
  lead: string;
  matrixTitle: string;
  matrixDesc: string;
  filterAll: string;
  filterHighTemp: string;
  colPolymer: string;
  colMesh: string;
  colMicron: string;
  colContaminants: string;
  colAlloy: string;
  stackEyebrow: string;
  stackTitle: string;
  stackDesc: string;
  stackNote: string;
  faqEyebrow: string;
  faqTitle: string;
  faqDesc: string;
  inlineCtaText: string;
}
const copyData: Record<Lang, CopySection> = {
  en: {
    eyebrow: "Technical Process Guide",
    h2: "Polymer melt filtration process guide",
    lead: "Select the right alloy, weave specification, and micron rating for continuous extrusion stability, minimal pressure drop, and clean resin output.",
    matrixTitle: "Polymer-specific mesh recommendation matrix",
    matrixDesc: "Verified operational parameters across continuous screen changers and melt blown systems.",
    filterAll: "All Polymers",
    filterHighTemp: "High-Temp / Fluoropolymers",
    colPolymer: "Polymer Base",
    colMesh: "Recommended Mesh / Weave",
    colMicron: "Micron Rating",
    colContaminants: "Targeted Contaminants",
    colAlloy: "Standard Alloy",
    stackEyebrow: "Standard Pack Stack",
    stackTitle: "Typical 5-layer screen pack cross-section",
    stackDesc: "Symmetrical and graduated mesh layering prevents hydraulic collapse under viscous polymer shear loads.",
    stackNote: "Diffusion bonded (sintered) or spot-welded rimmed packs are custom manufactured to match breaker plate diameters from 30mm to 450mm.",
    faqEyebrow: "Operational Guidelines",
    faqTitle: "Extrusion troubleshooting and process FAQ",
    faqDesc: "Critical factors affecting mesh service life, clean Delta P margins, and resin yield.",
    inlineCtaText: "Need custom mesh packs or differential pressure evaluation for your polymer extrusion line? Our team validates wire diameter, open area, and alloy compatibility."
  },
  zh: {
    eyebrow: "技术工艺指南",
    h2: "聚合物熔体过滤工艺指南",
    lead: "选择合适的合金材质、编织规格与微米级过滤精度，确保连续挤出稳定性、极低压降与洁净树脂产出。",
    matrixTitle: "聚合物专用滤网选型推荐表",
    matrixDesc: "经连续换网器与熔喷系统验证的实际运行参数。",
    filterAll: "全部聚合物",
    filterHighTemp: "耐高温 / 氟聚合物",
    colPolymer: "聚合物基材",
    colMesh: "推荐网目 / 编织形式",
    colMicron: "过滤精度",
    colContaminants: "主要拦截杂质",
    colAlloy: "标准合金材质",
    stackEyebrow: "标准复合网组结构",
    stackTitle: "典型 5 层挤出滤网横截面结构",
    stackDesc: "对称与渐变式网层排布，防止高粘度聚合物剪切应力下的液压塌陷与冲网变形。",
    stackNote: "扩散键合（烧结）或点焊包边滤网片可按多孔板尺寸定制（直径 30mm 至 450mm）。",
    faqEyebrow: "操作指南与问答",
    faqTitle: "挤出故障排除与工艺常见问题",
    faqDesc: "影响滤网使用寿命、清洁压差余量和树脂产率的关键因素。",
    inlineCtaText: "需要为您的高分子产线定制滤网组或计算压降吗？我们的工程团队将根据您的熔体温度与粘度曲线，验证丝径、开孔率与合金适配性。"
  },
  es: {
    eyebrow: "Guía de procesos técnicos",
    h2: "Guía de procesos de filtración de polímeros fundidos",
    lead: "Seleccione la aleación, especificación de tejido y retención micrométrica adecuadas para garantizar estabilidad de extrusión, mínima caída de presión y resinas puras.",
    matrixTitle: "Matriz recomendada de telas metálicas por polímero",
    matrixDesc: "Parámetros operativos comprobados en cambiadores continuos de filtros y líneas de soplado de masa fundida.",
    filterAll: "Todos los polímeros",
    filterHighTemp: "Alta temperatura / Fluoropolímeros",
    colPolymer: "Base polimérica",
    colMesh: "Malla recomendada / Tejido",
    colMicron: "Grado de filtración",
    colContaminants: "Contaminantes objetivo",
    colAlloy: "Aleación estándar",
    stackEyebrow: "Estructura estándar de paquete",
    stackTitle: "Sección transversal típica de paquete de 5 capas",
    stackDesc: "La disposición simétrica y graduada previene el colapso hidráulico bajo esfuerzos cortantes de polímeros viscosos.",
    stackNote: "Los paquetes sinterizados o con borde soldado por puntos se fabrican a medida para placas rompedoras de 30 mm a 450 mm.",
    faqEyebrow: "Pautas operativas",
    faqTitle: "Resolución de problemas de extrusión y preguntas frecuentes",
    faqDesc: "Factores críticos que inciden en la vida útil de la malla, márgenes de Delta P y rendimiento de resina.",
    inlineCtaText: "¿Requiere paquetes de filtros a medida o cálculo de caída de presión para su extrusora? Nuestro equipo valida diámetros de alambre, área abierta y compatibilidad de aleación."
  },
  de: {
    eyebrow: "Technischer Prozessleitfaden",
    h2: "Leitfaden zur Schmelzefiltration von Polymeren",
    lead: "Wählen Sie die passende Legierung, Gewebekonfiguration und Mikronfeinheit für kontinuierliche Extrusionsstabilität, geringen Differenzdruck und reinen Schmelzefluss.",
    matrixTitle: "Empfehlungsmatrix für polymerspezifische Drahtgewebe",
    matrixDesc: "Verifizierte Betriebsparameter für kontinuierliche Siebwechsler und Meltblown-Systeme.",
    filterAll: "Alle Polymere",
    filterHighTemp: "Hochtemperatur / Fluorpolymere",
    colPolymer: "Polymer-Basis",
    colMesh: "Empfohlenes Gewebe / Bindung",
    colMicron: "Filterfeinheit",
    colContaminants: "Zielpartikel & Verunreinigungen",
    colAlloy: "Standardlegierung",
    stackEyebrow: "Standard-Siebpaketaufbau",
    stackTitle: "Typischer Querschnitt eines 5-lagigen Siebpakets",
    stackDesc: "Symmetrische und abgestufte Maschenanordnung verhindert hydraulischen Kollaps unter viskosen Schmelzescherkräften.",
    stackNote: "Diffusionsgebundene (gesinterte) oder randverschweißte Siebpakete werden exakt passend für Lochplattendurchmesser von 30 mm bis 450 mm gefertigt.",
    faqEyebrow: "Betriebsrichtlinien",
    faqTitle: "Extrusionsoptimierung und technische FAQ",
    faqDesc: "Wesentliche Faktoren für Standzeit, Differenzdruckreserve und Harzausbeute.",
    inlineCtaText: "Benötigen Sie maßgeschneiderte Siebpakete oder eine Druckverlustanalyse für Ihre Extrusionslinie? Wir prüfen Drahtdurchmesser, freie Fläche und Werkstoffeignung."
  },
  fr: {
    eyebrow: "Guide technique des procédés",
    h2: "Guide technique de filtration des polymères en fusion",
    lead: "Sélectionnez l'alliage adéquat, le tissage et le seuil de coupure micrométrique pour garantir la stabilité de l'extrusion, une perte de charge minimale et des polymères purs.",
    matrixTitle: "Matrice de sélection des toiles par polymère",
    matrixDesc: "Paramètres opérationnels vérifiés sur changeurs de filtres continus et lignes meltblown.",
    filterAll: "Tous les polymères",
    filterHighTemp: "Haute température / Fluoropolymères",
    colPolymer: "Base polymère",
    colMesh: "Toile recommandée / Tissage",
    colMicron: "Seuil de filtration",
    colContaminants: "Polluants cibles",
    colAlloy: "Alliage standard",
    stackEyebrow: "Structure standard du pack filtrant",
    stackTitle: "Coupe transversale typique d'un pack 5 couches",
    stackDesc: "L'empilement symétrique et gradué évite l'effondrement hydraulique sous les fortes contraintes de cisaillement.",
    stackNote: "Les packs frittés ou bordés par soudage par points sont fabriqués sur mesure pour des plaques de rupture de 30 mm à 450 mm.",
    faqEyebrow: "Recommandations opérationnelles",
    faqTitle: "Dépannage d'extrusion et questions fréquentes",
    faqDesc: "Facteurs critiques déterminant la durée de service des toiles, la marge Delta P et le rendement matière.",
    inlineCtaText: "Besoin de packs filtrants sur mesure ou d'un calcul de perte de charge pour votre ligne d'extrusion ? Notre équipe valide diamètre de fil, surface ouverte et alliage."
  },
  pt: {
    eyebrow: "Guia de processos técnicos",
    h2: "Guia de processos de filtração de polímeros fundidos",
    lead: "Selecione a liga adequada, a especificação da malha e a retenção micrométrica para estabilidade contínua na extrusão, mínima perda de carga e resinas limpas.",
    matrixTitle: "Matriz recomendada de telas metálicas por polímero",
    matrixDesc: "Parâmetros operacionais verificados em trocadores contínuos de telas e linhas meltblown.",
    filterAll: "Todos os polímeros",
    filterHighTemp: "Alta temperatura / Fluorpolímeros",
    colPolymer: "Base polimérica",
    colMesh: "Malha recomendada / Tecelagem",
    colMicron: "Grau de filtração",
    colContaminants: "Contaminantes retidos",
    colAlloy: "Liga padrão",
    stackEyebrow: "Estrutura padrão de telas",
    stackTitle: "Corte transversal típico de conjunto de 5 camadas",
    stackDesc: "A disposição simétrica e gradual previne o colapso hidráulico sob tensões de cisalhamento de polímeros viscosos.",
    stackNote: "Pacotes sinterizados ou com borda soldada por pontos são produzidos sob medida para placas suporte de 30 mm a 450 mm.",
    faqEyebrow: "Diretrizes operacionais",
    faqTitle: "Solução de problemas de extrusão e perguntas frequentes",
    faqDesc: "Fatores críticos que afetam a vida útil da tela, margens de Delta P e rendimento da resina.",
    inlineCtaText: "Necessita de conjuntos de telas sob medida ou cálculo de perda de carga para sua extrusora? Nossa equipe valida diâmetros de arame, área aberta e compatibilidade de ligas."
  }
};
interface Pillar {
  id: string;
  icon: React.ComponentType<{
    className?: string;
    "aria-hidden"?: boolean | "true" | "false";
  }>;
  title: Record<Lang, string>;
  subtitle: Record<Lang, string>;
  summary: Record<Lang, string>;
  specs: {
    label: Record<Lang, string>;
    value: Record<Lang, string>;
  }[];
}
const processPillars: Pillar[] = [{
  id: "temp",
  icon: Thermometer,
  title: {
    en: "Thermal resistance & alloys",
    zh: "耐温性与合金选型",
    es: "Resistencia térmica y selección de aleaciones",
    de: "Temperaturbeständigkeit & Legierungsauswahl",
    fr: "Résistance thermique et choix des alliages",
    pt: "Resistência térmica e seleção de ligas"
  },
  subtitle: {
    en: "Thermal stability in elevated melt environments",
    zh: "熔融环境中的热稳定性与合金适应性",
    es: "Estabilidad térmica en entornos de masa fundida",
    de: "Thermische Beständigkeit in Schmelzeumgebungen",
    fr: "Stabilité thermique en environnement fondu",
    pt: "Estabilidade térmica em ambientes fundidos"
  },
  summary: {
    en: "Polymer extruders exert sustained heat and high hydrostatic pressures. Choosing the exact grade preserves structural weave aperture without wire elongation.",
    zh: "聚合物挤出机施加持续高温与高静水压力。选择精确牌号可在无金属丝拉伸的情况下保持结构织物孔径。",
    es: "Las extrusoras ejercen calor continuo y presiones hidrostáticas elevadas. Elegir el grado exacto preserva la apertura del tejido sin elongación del alambre.",
    de: "Extruder erzeugen Dauerhitze und hohe hydrostatische Drücke. Die richtige Legierung sichert Maschenöffnungen ohne Drahtdehnung.",
    fr: "Les extrudeuses appliquent une chaleur continue et de fortes pressions hydrostatiques. Choisir la bonne nuance préserve les ouvertures sans élongation.",
    pt: "As extrusoras exercem calor contínuo e altas pressões hidrostáticas. Escolher a liga correta preserva as aberturas sem alongamento do arame."
  },
  specs: [{
    label: {
      en: "Standard Alloys",
      zh: "标准合金",
      es: "Aleaciones estándar",
      de: "Standardlegierungen",
      fr: "Alliages standards",
      pt: "Ligas padrão"
    },
    value: {
      en: "304 & 316L Stainless Steel",
      zh: "304 与 316L 不锈钢",
      es: "Acero inoxidable 304 y 316L",
      de: "Edelstahl 304 & 316L",
      fr: "Acier inoxydable 304 et 316L",
      pt: "Aço inoxidável 304 e 316L"
    }
  }, {
    label: {
      en: "High-Heat Alloys",
      zh: "耐高温合金",
      es: "Aleaciones para alta temperatura",
      de: "Hochtemperatur-Legierungen",
      fr: "Alliages haute température",
      pt: "Ligas para alta temperatura"
    },
    value: {
      en: "310S & Inconel 600",
      zh: "310S 与 Inconel 600",
      es: "310S e Inconel 600",
      de: "310S & Inconel 600",
      fr: "310S et Inconel 600",
      pt: "310S e Inconel 600"
    }
  }, {
    label: {
      en: "Corrosion Factor",
      zh: "耐腐蚀系数",
      es: "Factor de corrosión",
      de: "Korrosionsbeständigkeit",
      fr: "Résistance à la corrosion",
      pt: "Fator de corrosão"
    },
    value: {
      en: "316L resists acidic additives and flame retardants",
      zh: "316L 耐酸性添加剂与阻燃剂",
      es: "316L resiste aditivos ácidos y retardantes de llama",
      de: "316L widersteht sauren Additiven und Flammschutzmitteln",
      fr: "Le 316L résiste aux additifs acides et ignifugeants",
      pt: "316L resiste a aditivos ácidos e retardantes de chama"
    }
  }]
}, {
  id: "mesh",
  icon: Grid,
  title: {
    en: "Mesh count & weave architecture",
    zh: "目数与编织结构",
    es: "Densidad de malla y estructura del tejido",
    de: "Maschenzahl & Gewebearchitektur",
    fr: "Nombre de mailles et architecture de tissage",
    pt: "Contagem de malha e estrutura do tecido"
  },
  subtitle: {
    en: "Balancing differential pressure and throughput",
    zh: "平衡压差与熔体通量",
    es: "Equilibrio entre caída de presión y caudal",
    de: "Abstimmung von Differenzdruck und Durchsatz",
    fr: "Équilibre entre perte de charge et débit",
    pt: "Equilíbrio entre pressão diferencial e vazão"
  },
  summary: {
    en: "Melt viscosity demands robust woven structures. Dutch weaves and sintered composite packs resist high shear stresses without aperture deformation or wire migration.",
    zh: "熔体粘度要求坚固的编织结构。密纹荷兰织网与多层烧结复合网组可承受高剪切应力，无网孔变形或丝线移位。",
    es: "La viscosidad exige estructuras robustas. Las mallas holandesas y paquetes sinterizados resisten altos esfuerzos cortantes sin deformar poros ni desplazar alambres.",
    de: "Schmelzeviskosität verlangt widerstandsfähige Gewebe. Tressengewebe und gesinterte Verbundpakete widerstehen Scherspannungen formstabil ohne Drahtverschiebung.",
    fr: "La viscosité requiert des toiles robustes. Les toiles hollandaises et packs frittés résistent aux forts cisaillements sans déformation de pore ni migration de fil.",
    pt: "A viscosidade exige estruturas robustas. Telas holandesas e pacotes sinterizados resistem a alto cisalhamento sem deformação de abertura ou deslocamento."
  },
  specs: [{
    label: {
      en: "Coarse Support Layer",
      zh: "粗支承层",
      es: "Capa soporte gruesa",
      de: "Grobe Stützschicht",
      fr: "Couche de support grossière",
      pt: "Camada suporte grossa"
    },
    value: {
      en: "20 to 60 Mesh (structural backing)",
      zh: "20 至 60 目（结构背衬）",
      es: "20 a 60 mesh (respaldo estructural)",
      de: "20 bis 60 Mesh (strukturelle Stützung)",
      fr: "20 à 60 mesh (renfort structurel)",
      pt: "20 a 60 mesh (suporte estrutural)"
    }
  }, {
    label: {
      en: "Filtration Control Layer",
      zh: "过滤控制层",
      es: "Capa de control de filtración",
      de: "Filtrationsschicht",
      fr: "Couche filtrante de contrôle",
      pt: "Camada de controle de filtração"
    },
    value: {
      en: "80 to 500 Mesh (Plain / Twill Dutch)",
      zh: "80 至 500 目（平纹/斜纹荷兰织）",
      es: "80 a 500 mesh (holandesa lisa / cruzada)",
      de: "80 bis 500 Mesh (Glatte / Köpertresse)",
      fr: "80 à 500 mesh (hollandaise unie / croisée)",
      pt: "80 a 500 mesh (holandesa lisa / cruzada)"
    }
  }, {
    label: {
      en: "Sintered Stacks",
      zh: "烧结网组",
      es: "Paquetes sinterizados",
      de: "Sinterverbunde",
      fr: "Packs frittés",
      pt: "Conjuntos sinterizados"
    },
    value: {
      en: "3 to 5 layers diffusion bonded for zero shifting",
      zh: "3 至 5 层扩散键合，零位移",
      es: "3 a 5 capas unidas por difusión sin desplazamiento",
      de: "3 bis 5 Lagen diffusionsgebunden für null Verschiebung",
      fr: "3 à 5 couches soudées par diffusion sans mouvement",
      pt: "3 a 5 camadas unidas por difusão sem deslocamento"
    }
  }]
}, {
  id: "contaminants",
  icon: Filter,
  title: {
    en: "Contaminant removal & retention",
    zh: "杂质截留与颗粒去除",
    es: "Eliminación y retención de impurezas",
    de: "Partikelrückhaltung & Verunreinigungsschutz",
    fr: "Rétention et élimination des impuretés",
    pt: "Remoção e retenção de contaminantes"
  },
  subtitle: {
    en: "Eliminating gels, carbon specks, and unmelts",
    zh: "消除凝胶、碳点与未熔颗粒",
    es: "Eliminación de geles, puntos de carbono y no fundidos",
    de: "Beseitigung von Gelen, Kohlenstoffpunkten und Ungeschmolzenem",
    fr: "Élimination des gels, points de carbone et infondus",
    pt: "Eliminação de géis, pontos de carbono e não fundidos"
  },
  summary: {
    en: "Polymer purity depends on capturing both rigid solids and deformable gel particles before the melt stream reaches spinning nozzles or extrusion dies.",
    zh: "聚合物纯度取决于在熔体流到达纺丝喷丝头或挤出模头前，有效截留刚性固体与可变形凝胶颗粒。",
    es: "La pureza del polímero depende de retener sólidos rígidos y geles deformables antes de llegar a las hileras de hilatura o boquillas de extrusión.",
    de: "Polymerreinheit verlangt das Zurückhalten starrer Partikel und verformbarer Gele, bevor die Schmelze Spinndüsen oder Werkzeuge erreicht.",
    fr: "La pureté du polymère repose sur la capture des solides rigides et des gels déformables avant les filières de filage ou têtes d'extrusion.",
    pt: "A pureza do polímero exige reter sólidos rígidos e géis deformáveis antes que o fluxo chegue às fieiras ou matrizes de extrusão."
  },
  specs: [{
    label: {
      en: "Rigid Inclusions",
      zh: "刚性夹杂物",
      es: "Inclusiones rígidas",
      de: "Starre Einschlüsse",
      fr: "Inclusions rigides",
      pt: "Inclusões rígidas"
    },
    value: {
      en: "High retention reliability at rated micron",
      zh: "额定微米精度下可靠高效截留",
      es: "Alta fiabilidad de retención en el micraje nominal",
      de: "Hohe Rückhaltegenauigkeit bei Nennfeinheit",
      fr: "Haute fiabilité de rétention au micron nominal",
      pt: "Alta confiabilidade de retenção no micrômetro nominal"
    }
  }, {
    label: {
      en: "Gel Capture",
      zh: "凝胶捕集",
      es: "Captura de geles",
      de: "Gelfiltration",
      fr: "Capture des gels",
      pt: "Captura de géis"
    },
    value: {
      en: "Multi-layer depth capture via tortuous Dutch path",
      zh: "通过曲折荷兰孔道进行多层深层捕集",
      es: "Retención en profundidad multicapa por paso holandés sinuoso",
      de: "Tiefenfiltration über gewundene Tressenkanäle",
      fr: "Capture en profondeur multicouche par canaux tortueux",
      pt: "Captura em profundidade multicamada por canais sinuosos"
    }
  }, {
    label: {
      en: "Clean Extrusion Life",
      zh: "洁净挤出寿命",
      es: "Vida útil en extrusión limpia",
      de: "Standzeit bei sauberer Extrusion",
      fr: "Durée de vie en extrusion",
      pt: "Vida útil em extrusão limpa"
    },
    value: {
      en: "Extended cycle time between pack changes",
      zh: "延长换网器滤网更换周期",
      es: "Mayor intervalo operativo entre cambios de paquete",
      de: "Verlängerte Standzeiten zwischen Siebwechseln",
      fr: "Intervalles prolongés entre changements de packs",
      pt: "Maior tempo de ciclo entre trocas de telas"
    }
  }]
}];
interface FAQItem {
  question: Record<Lang, string>;
  answer: Record<Lang, string>;
}
const faqs: FAQItem[] = [{
  question: {
    en: "Why choose multi-layer screen packs over single-layer mesh in polymer extrusion?",
    zh: "为什么在聚合物挤出中优先选用多层组合滤网而非单层网？",
    es: "¿Por qué se prefieren paquetes de filtros multicapa frente a mallas monocapa en extrusión de polímeros?",
    de: "Warum werden bei der Polymerextrusion mehrlagige Siebpakete gegenüber einlagigem Gewebe bevorzugt?",
    fr: "Pourquoi privilégier les packs filtrants multicouches plutôt qu'une toile monocouche en extrusion de polymères ?",
    pt: "Por que escolher pacotes de telas multicamadas em vez de tela monocamada na extrusão de polímeros?"
  },
  answer: {
    en: "Single-layer fine mesh can flex or rupture under the differential pressures of viscous polymers. Multi-layer screen packs sandwich the fine filtration weave between coarser support and breaker meshes, providing hydraulic stability and preventing wire blowout.",
    zh: "单层细目滤网在高粘度聚合物的高压差下容易发生形变或破裂。多层组合滤网将精密过滤网夹在较粗的支撑网和多孔板保护网之间，提供优异的液压支撑稳定性并防止吹网。",
    es: "Una malla fina de una sola capa puede deformarse o romperse ante las presiones diferenciales de polímeros viscosos. Los paquetes multicapa colocan el tejido fino entre mallas gruesas de soporte y rotura, aportando estabilidad hidráulica y evitando roturas.",
    de: "Feine Einzellagengewebe können sich unter den Differenzdrücken hochviskoser Polymere verformen oder reißen. Mehrlagige Siebpakete betten das feine Filtergewebe zwischen gröbere Stütz- und Lochplattengitter ein, was hydraulische Stabilität schafft und Gewebebruch verhindert.",
    fr: "Une toile fine monocouche peut fléchir ou rompre sous les fortes pressions différentielles de polymères visqueux. Les packs multicouches enserrent la toile de filtration fine entre des toiles de support plus grossières, offrant une stabilité hydraulique et évitant le déchirement.",
    pt: "Telas finas de camada única podem ceder ou romper sob altas pressões diferenciais de polímeros viscosos. Pacotes multicamadas intercalam a malha fina entre telas grossas de suporte, conferindo estabilidade hidráulica e prevenindo rupturas."
  }
}, {
  question: {
    en: "How does melt index (MFI) influence mesh selection?",
    zh: "熔体流动速率 (MFI) 如何影响滤网选型？",
    es: "¿Cómo influye el índice de fluidez (MFI) en la selección de la malla?",
    de: "Wie beeinflusst der Schmelzflussindex (MFI) die Gewebeauswahl?",
    fr: "Comment l'indice de fluidité à chaud (MFI) influence-t-il le choix de la toile ?",
    pt: "Como o índice de fluidez (MFI) influencia a escolha da tela?"
  },
  answer: {
    en: "High viscosity polymers (low MFI) generate high shear stress and elevated Delta P across the filter. These require high-strength sintered or reverse Dutch weaves with wider open areas. Lower viscosity melts (high MFI) can utilize tighter twilled Dutch weaves for finer micron ratings with lower pressure penalties.",
    zh: "高粘度聚合物（低 MFI）会在滤网上产生高剪切应力与较大压降 (Delta P)，需选用开孔率高、强度大的烧结网或反向荷兰织网；低粘度熔体（高 MFI）则可采用更紧密的斜纹荷兰织网以获得更高的微米精度，且不会造成过大压差。",
    es: "Polímeros de alta viscosidad (bajo MFI) generan elevados esfuerzos de cizallamiento y Delta P sobre el filtro, requiriendo mallas sinterizadas resistentes o de paso holandés inverso con mayor área abierta. Fundidos más fluidos (alto MFI) admiten mallas holandesas cruzadas más tupidas para una filtración más fina con menor penalización de presión.",
    de: "Hochviskose Polymere (niedriger MFI) erzeugen hohe Scherspannungen und hohe Druckverluste (Delta P). Hierfür sind hochfeste Sintergewebe oder Panzertressengewebe mit größerer freier Fläche erforderlich. Niedrigviskose Schmelzen (hoher MFI) können engere Köpertressen für feinere Mikronwerte nutzen.",
    fr: "Les polymères à haute viscosité (faible MFI) induisent des contraintes de cisaillement et des pertes de charge élevées. Ils exigent des toiles frittées renforcées ou des toiles hollandaises inversées à grande surface ouverte. Les fluides plus fluides (MFI élevé) tolèrent des toiles croisées plus serrées sans surpression excessive.",
    pt: "Polímeros de alta viscosidade (baixo MFI) geram alto cisalhamento e perda de carga elevada. Exigem malhas sinterizadas resistentes ou holandesas reversas com maior área livre. Fundidos de menor viscosidade (alto MFI) utilizam malhas holandesas cruzadas para retenção mais fina."
  }
}, {
  question: {
    en: "What causes gel bleed-through and how can woven wire mesh prevent it?",
    zh: "什么导致晶点/凝胶穿透，金属丝网如何防止？",
    es: "¿Qué provoca el paso de geles y cómo lo previene la malla metálica tejida?",
    de: "Was verursacht das Durchtreten von Gelen und wie verhindert Drahtgewebe dies?",
    fr: "Qu'est-ce qui cause la traversée des gels et comment la toile métallique tissée l'évite-t-elle ?",
    pt: "O que causa a passagem de géis e como a malha tecida previne isso?"
  },
  answer: {
    en: "Gels are semi-solid polymer chains that deform under high pressure. Standard square weave allows gels to elongate and squeeze through openings. Dutch weaves create a tortuous three-dimensional flow path that traps deformable gels in the wire matrix through shear dissipation.",
    zh: "凝胶是在高压下会发生形变的半固态聚合物分子链。标准方孔网容易让凝胶拉长挤压穿过滤孔。密纹荷兰织网构成了曲折的三维流动通道，通过剪切耗散将易变形凝胶截留在金属丝孔隙结构内。",
    es: "Los geles son cadenas poliméricas semisólidas que se deforman bajo alta presión. La malla cuadrada estándar permite que se estiren y atraviesen las aberturas. Los tejidos holandeses crean trayectorias tridimensionales tortuosas que atrapan los geles deformables disipando la cizalladura.",
    de: "Gele sind halbfeste Polymerketten, die sich unter Druck verformen. Quadratmaschen lassen diese langgestreckt durchtreten. Tressengewebe bilden gewundene dreidimensionale Fließwege, die verformbare Gele durch Scherenergiedissipation im Drahtgefüge fangen.",
    fr: "Les gels sont des chaînes polymères semi-solides qui se déforment sous haute pression. Une maille carrée standard permet aux gels de s'étirer et de traverser. Les toiles hollandaises créent un parcours tortueux en trois dimensions qui piège les gels dans la matrice métallique par dissipation de cisaillement.",
    pt: "Géis são cadeias poliméricas semissólidas que se deformam sob alta pressão. A malha quadrada permite que se alonguem e atravessem. As telas holandesas criam caminhos sinuosos tridimensionais que retêm esses géis através de dissipação por cisalhamento."
  }
}, {
  question: {
    en: "Which alloy is recommended for halogenated or flame-retardant polymers?",
    zh: "含卤素或添加阻燃剂的聚合物推荐选用哪种合金？",
    es: "¿Qué aleación se recomienda para polímeros halogenados o ignífugos?",
    de: "Welche Legierung wird für halogenierte oder flammgeschützte Polymere empfohlen?",
    fr: "Quel alliage est recommandé pour les polymères halogénés ou ignifugés ?",
    pt: "Qual liga é recomendada para polímeros halogenados ou com retardantes de chama?"
  },
  answer: {
    en: "For polymers releasing acidic vapors or carrying abrasive flame retardants (like PVC, fluoropolymers, or filled nylon), we specify molybdenum-stabilized 316L or specialty nickel alloys to prevent intergranular stress corrosion.",
    zh: "对于会释放酸性气体或含有磨蚀性阻燃剂的聚合物（如 PVC、氟聚合物或填充尼龙），我们推荐采用含钼稳定的 316L 不锈钢或特种耐热耐蚀镍基合金，以防止晶间应力腐蚀。",
    es: "Para polímeros que desprenden vapores ácidos o contienen retardantes abrasivos (como PVC, fluoropolímeros o poliamidas con carga), prescribimos acero 316L estabilizado con molibdeno o aleaciones de níquel para evitar la corrosión intergranular.",
    de: "Für Polymere, die saure Gase freisetzen oder abrasive Flammschutzmittel enthalten (z. B. PVC, Fluorpolymere oder gefülltes Polyamid), empfehlen wir molybdänstabilisierten Edelstahl 316L oder Nickelbasislegierungen gegen interkristalline Spannungsrisskorrosion.",
    fr: "Pour les polymères dégageant des vapeurs acides ou contenant des retardateurs de flamme abrasifs (tels le PVC, les fluoropolymères ou les nylons chargés), nous préconisons l'acier inoxydable 316L au molybdène ou des alliages de nickel pour éviter la corrosion sous contrainte.",
    pt: "Para polímeros que liberam vapores ácidos ou contêm retardantes abrasivos (como PVC, fluorpolímeros ou náilon com carga), recomendamos aço 316L com molibdênio ou ligas especiais de níquel para evitar corrosão sob tensão."
  }
}];
interface StackLayer {
  layer: Record<Lang, string>;
  role: Record<Lang, string>;
  primary?: boolean;
}
const stackLayers: StackLayer[] = [{
  layer: {
    en: "Layer 1: 20 Mesh (0.45mm wire)",
    zh: "第 1 层: 20 目 (0.45mm 丝径)",
    es: "Capa 1: 20 mesh (alambre 0,45 mm)",
    de: "Lage 1: 20 Mesh (0,45 mm Draht)",
    fr: "Couche 1 : 20 mesh (fil de 0,45 mm)",
    pt: "Camada 1: 20 mesh (arame 0,45 mm)"
  },
  role: {
    en: "Breaker Support",
    zh: "多孔板支撑",
    es: "Soporte de placa rompedora",
    de: "Stützung Lochplatte",
    fr: "Support plaque de rupture",
    pt: "Suporte placa furada"
  }
}, {
  layer: {
    en: "Layer 2: 60 Mesh (0.18mm wire)",
    zh: "第 2 层: 60 目 (0.18mm 丝径)",
    es: "Capa 2: 60 mesh (alambre 0,18 mm)",
    de: "Lage 2: 60 Mesh (0,18 mm Draht)",
    fr: "Couche 2 : 60 mesh (fil de 0,18 mm)",
    pt: "Camada 2: 60 mesh (arame 0,18 mm)"
  },
  role: {
    en: "Intermediate Cushion",
    zh: "过渡缓冲",
    es: "Amortiguador intermedio",
    de: "Zwischenpolsterung",
    fr: "Amortisseur intermédiaire",
    pt: "Amortecedor intermediário"
  }
}, {
  layer: {
    en: "Layer 3: Twill Dutch Weave (e.g. 200x600 to 325x2300)",
    zh: "第 3 层：斜纹荷兰编织网（如 200×600 至 325×2300）",
    es: "Capa 3: Malla holandesa cruzada (ej. 200x600 a 325x2300)",
    de: "Lage 3: Köpertressengewebe (z. B. 200x600 bis 325x2300)",
    fr: "Couche 3 : Toile hollandaise croisée (ex. 200x600 à 325x2300)",
    pt: "Camada 3: Malha holandesa cruzada (ex. 200x600 a 325x2300)"
  },
  role: {
    en: "Primary Control (20–40µm)",
    zh: "主控过滤层 (20–40µm)",
    es: "Control principal (20–40µm)",
    de: "Hauptkontrollschicht (20–40µm)",
    fr: "Contrôle principal (20–40µm)",
    pt: "Controle principal (20–40µm)"
  },
  primary: true
}, {
  layer: {
    en: "Layer 4: 60 Mesh (0.18mm wire)",
    zh: "第 4 层: 60 目 (0.18mm 丝径)",
    es: "Capa 4: 60 mesh (alambre 0,18 mm)",
    de: "Lage 4: 60 Mesh (0,18 mm Draht)",
    fr: "Couche 4 : 60 mesh (fil de 0,18 mm)",
    pt: "Camada 4: 60 mesh (arame 0,18 mm)"
  },
  role: {
    en: "Cushion / Flow Straightener",
    zh: "缓冲 / 整流层",
    es: "Amortiguador / Homogeneizador",
    de: "Dämpfung / Strömungsrichter",
    fr: "Amortisseur / Régulateur d'écoulement",
    pt: "Amortecedor / Alinhador de fluxo"
  }
}, {
  layer: {
    en: "Layer 5: 20 Mesh (0.45mm wire)",
    zh: "第 5 层: 20 目 (0.45mm 丝径)",
    es: "Capa 5: 20 mesh (alambre 0,45 mm)",
    de: "Lage 5: 20 Mesh (0,45 mm Draht)",
    fr: "Couche 5 : 20 mesh (fil de 0,45 mm)",
    pt: "Camada 5: 20 mesh (arame 0,45 mm)"
  },
  role: {
    en: "Inlet Coarse Guard",
    zh: "入口粗滤保护",
    es: "Protección gruesa de entrada",
    de: "Einlass-Grobschutz",
    fr: "Protection d'entrée grossière",
    pt: "Proteção de entrada grossa"
  }
}];
export default function PolymerMeltProcessGuide() {
  const {
    lang
  } = useLanguage();
  const [activeFilter, setActiveFilter] = React.useState<string>("all");
  const c = pick(lang, copyData);
  const filterButtons = [{
    label: c.filterAll,
    value: "all"
  }, {
    label: "PET",
    value: "pet"
  }, {
    label: "PP / PE",
    value: "pp"
  }, {
    label: "PA6 / PA66",
    value: "pa6"
  }, {
    label: c.filterHighTemp,
    value: "ptfe"
  }];
  const filteredSpecs = useMemo(() => {
    return activeFilter === "all" ? polymerData : polymerData.filter(item => item.shortName.toLowerCase().includes(activeFilter.toLowerCase()));
  }, [activeFilter]);
  const columns: DataTableColumn<PolymerSpec>[] = useMemo(() => [{
    key: "polymer",
    header: c.colPolymer,
    mobileTitle: true,
    cell: row => <div>
          <div className="font-medium text-foreground">{pick(lang, row.polymer)}</div>
          <span className="inline-block mf-caption font-mono text-primary font-semibold mt-0.5">
            {row.shortName}
          </span>
        </div>
  }, {
    key: "mesh",
    header: c.colMesh,
    cell: row => <span className="text-foreground/90 font-medium">{pick(lang, row.recommendedMesh)}</span>
  }, {
    key: "micron",
    header: c.colMicron,
    cell: row => <span className="font-mono font-semibold text-primary">{row.targetMicron}</span>
  }, {
    key: "contaminants",
    header: c.colContaminants,
    cell: row => <span className="text-muted-foreground whitespace-normal">
          {pick(lang, row.keyContaminants)}
        </span>
  }, {
    key: "alloy",
    header: c.colAlloy,
    cell: row => <Badge variant="outline" className="font-mono mf-caption border border-border bg-card">
          {row.recommendedAlloy}
        </Badge>
  }], [c.colPolymer, c.colMesh, c.colMicron, c.colContaminants, c.colAlloy, lang]);
  return <section id="polymermeltprocessguide" data-nav="light" className="bg-muted/50 mf-section border-b border-border text-foreground transition-colors">
      <div className="mf-container">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-mono mf-caption uppercase tracking-wider text-muted-foreground border-border bg-card">
            {c.eyebrow}
          </Badge>
          <h2 className="mf-h2 text-foreground font-default">
            {c.h2}
          </h2>
          <p className="mt-4 mf-lead text-muted-foreground">
            {c.lead}
          </p>
        </div>

        {/* 3 Core Engineering Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {processPillars.map((pillar, i) => {
          const IconComp = pillar.icon;
          return <Reveal key={pillar.id} delay={Math.min(i, 3) * 100} className="flex">
                <Card className="border border-border flex-1 flex flex-col justify-between hover:border-primary/40 transition-colors bg-card">
                  <CardHeader>
                    <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary mb-4">
                      <IconComp className="size-5" aria-hidden="true" />
                    </div>
                    <CardTitle className="mf-h3 text-foreground font-default">
                      {pick(lang, pillar.title)}
                    </CardTitle>
                    <CardDescription className="mf-caption font-mono uppercase tracking-wider text-primary mt-1 font-medium">
                      {pick(lang, pillar.subtitle)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {pick(lang, pillar.summary)}
                    </p>
                    <div className="pt-3 border-t border-border space-y-2">
                      {pillar.specs.map((spec, specIdx) => <div key={specIdx} data-index={specIdx} className="mf-caption flex flex-col sm:flex-row sm:justify-between py-1 border-b border-border/60 last:border-0 gap-1">
                          <span className="text-muted-foreground font-medium">{pick(lang, spec.label)}:</span>
                          <span className="text-foreground font-semibold text-left sm:text-right">{pick(lang, spec.value)}</span>
                        </div>)}
                    </div>
                  </CardContent>
                </Card>
              </Reveal>;
        })}
        </div>

        {/* Technical Specification Matrix */}
        <div className="mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-6 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <FileSpreadsheet className="size-5 text-primary" aria-hidden="true" />
                <h3 className="mf-h3 text-foreground font-default">
                  {c.matrixTitle}
                </h3>
              </div>
              <p className="text-sm text-muted-foreground">
                {c.matrixDesc}
              </p>
            </div>

            {/* Quick Filter Buttons */}
            <div className="flex flex-wrap gap-1.5">
              {filterButtons.map((btn, btnIdx) => <Button key={btn.value} data-index={btnIdx} variant={activeFilter === btn.value ? "default" : "outline"} onClick={() => setActiveFilter(btn.value)} className="mf-caption px-3 py-1.5 rounded-md font-medium transition-colors">
                  {btn.label}
                </Button>)}
            </div>
          </div>

          <DataTable columns={columns} rows={filteredSpecs} getRowKey={row => row.shortName} />
        </div>

        {/* Engineering Advisory + Screen Pack Configurations */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-start">
          {/* Diagnostic Pack Architecture Card */}
          <div className="lg:col-span-6 bg-card border border-border rounded-lg p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-3 text-primary">
              <Layers className="size-5" aria-hidden="true" />
              <span className="mf-caption font-mono uppercase tracking-wider font-semibold">
                {c.stackEyebrow}
              </span>
            </div>
            <h3 className="mf-h3 text-foreground font-default mb-2">
              {c.stackTitle}
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              {c.stackDesc}
            </p>

            <div className="space-y-3 font-mono mf-caption">
              {stackLayers.map((layer, idx) => {
              if (layer.primary) {
                return <div key={idx} className="p-3 bg-primary/10 rounded-md border border-primary/30 flex items-center justify-between">
                      <span className="font-bold text-primary">
                        {pick(lang, layer.layer)}
                      </span>
                      <Badge variant="default" className="mf-caption bg-primary text-primary-foreground">
                        {pick(lang, layer.role)}
                      </Badge>
                    </div>;
              }
              const bgClass = idx === 0 || idx === 4 ? "bg-muted/60" : "bg-muted/40";
              return <div key={idx} className={`p-3 ${bgClass} rounded-md border border-border flex items-center justify-between`}>
                    <span className="font-semibold text-foreground">
                      {pick(lang, layer.layer)}
                    </span>
                    <Badge variant="outline" className="mf-caption bg-background border-border">
                      {pick(lang, layer.role)}
                    </Badge>
                  </div>;
            })}
            </div>

            <div className="mt-6 pt-4 border-t border-border flex items-start gap-2.5 mf-caption text-muted-foreground">
              <Info className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
              <span>{c.stackNote}</span>
            </div>
          </div>

          {/* Operational Best Practices Accordion */}
          <div className="lg:col-span-6 bg-card border border-border rounded-lg p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-3 text-primary">
              <ShieldCheck className="size-5" aria-hidden="true" />
              <span className="mf-caption font-mono uppercase tracking-wider font-semibold">
                {c.faqEyebrow}
              </span>
            </div>
            <h3 className="mf-h3 text-foreground font-default mb-2">
              {c.faqTitle}
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              {c.faqDesc}
            </p>

            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, faqIdx) => <AccordionItem key={faqIdx} value={`faq-${faqIdx}`} data-index={faqIdx}>
                  <AccordionTrigger className="text-sm font-semibold text-foreground text-left py-3 hover:text-primary">
                    {pick(lang, faq.question)}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed pt-1 pb-3">
                    {pick(lang, faq.answer)}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>

        {/* Standard Mid-page Inline CTA Strip */}
        <InlineCta id="polymer-inline-cta" text={c.inlineCtaText} buttonLabel={pick(lang, COMMON.requestQuote)} to="/RequestAQuotePage" />
      </div>
    </section>;
}